import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus, 
  Save, 
  Trash2, 
  GripVertical, 
  Eye, 
  Copy,
  Type,
  Hash,
  AlignLeft,
  ChevronDown,
  CheckSquare,
  Calendar,
  Image,
  User,
  Users,
  ExternalLink,
  Settings2,
  MessageSquare,
  UserPlus,
  BarChart3,
  Search,
  Star
} from 'lucide-react';

const FIELD_TYPES = [
  { value: 'short_text', label: 'Short Text', icon: Type, description: 'Single line text input' },
  { value: 'long_text', label: 'Long Text', icon: AlignLeft, description: 'Multi-line text area' },
  { value: 'number', label: 'Number', icon: Hash, description: 'Numeric input' },
  { value: 'dropdown', label: 'Dropdown', icon: ChevronDown, description: 'Select from options' },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare, description: 'Multiple selection' },
  { value: 'date', label: 'Date', icon: Calendar, description: 'Date picker' },
  { value: 'rating', label: 'Rating Scale', icon: Star, description: 'Rating with custom labels' },
  { value: 'photo', label: 'Photo Upload', icon: Image, description: 'Image file upload' },
  { value: 'its_number', label: 'ITS Number (Auto-fetch)', icon: User, description: 'Fetches member data' },
  { value: 'hof_its_number', label: 'HOF ITS (Auto-fetch)', icon: Users, description: 'Fetches HOF data' },
];

// Auto-fetched fields options
const AUTO_FETCH_OPTIONS = [
  { key: 'full_name', label: 'Full Name' },
  { key: 'hof_name', label: 'HOF Name' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'mohalla', label: 'Mohalla' },
  { key: 'sector', label: 'Sector' },
  { key: 'age', label: 'Age' },
  { key: 'gender', label: 'Gender' },
  { key: 'address', label: 'Address' },
];

export default function FormBuilderPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { api, isSuperAdmin } = useAuth();
  
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form details
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [isOpen, setIsOpen] = useState(true);
  const [allowMultiple, setAllowMultiple] = useState(true);
  const [autoFetchFields, setAutoFetchFields] = useState(['full_name', 'mobile', 'mohalla', 'sector']);
  const [sendWhatsApp, setSendWhatsApp] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  
  // Fields
  const [fields, setFields] = useState([]);
  
  // Add field dialog
  const [showAddFieldDialog, setShowAddFieldDialog] = useState(false);
  const [newFieldType, setNewFieldType] = useState('');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldPlaceholder, setNewFieldPlaceholder] = useState('');
  const [newFieldOptions, setNewFieldOptions] = useState('');
  const [newFieldDescription, setNewFieldDescription] = useState('');
  // Rating field config
  const [newRatingMin, setNewRatingMin] = useState(1);
  const [newRatingMax, setNewRatingMax] = useState(5);
  const [newRatingStep, setNewRatingStep] = useState(1);
  const [newRatingLeftLabel, setNewRatingLeftLabel] = useState('');
  const [newRatingRightLabel, setNewRatingRightLabel] = useState('');
  
  // Edit field dialog
  const [showEditFieldDialog, setShowEditFieldDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editFieldLabel, setEditFieldLabel] = useState('');
  const [editFieldRequired, setEditFieldRequired] = useState(false);
  const [editFieldPlaceholder, setEditFieldPlaceholder] = useState('');
  const [editFieldOptions, setEditFieldOptions] = useState('');
  const [editFieldDescription, setEditFieldDescription] = useState('');
  // Edit rating field config
  const [editRatingMin, setEditRatingMin] = useState(1);
  const [editRatingMax, setEditRatingMax] = useState(5);
  const [editRatingStep, setEditRatingStep] = useState(1);
  const [editRatingLeftLabel, setEditRatingLeftLabel] = useState('');
  const [editRatingRightLabel, setEditRatingRightLabel] = useState('');
  
  // Settings dialog
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  
  // User access dialog
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  // Filter users based on search query
  const filteredUsers = availableUsers.filter(user => {
    if (!userSearchQuery.trim()) return true;
    const query = userSearchQuery.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.its_id?.toLowerCase().includes(query)
    );
  });

  const fetchForm = useCallback(async () => {
    try {
      const response = await api.get(`/forms/${formId}`);
      const formData = response.data;
      setForm(formData);
      setFormName(formData.name || '');
      setFormDescription(formData.description || '');
      setIsOpen(formData.is_open !== false);
      setAllowMultiple(formData.allow_multiple_submissions !== false);
      setAutoFetchFields(formData.auto_fetch_fields || ['full_name', 'mobile', 'mohalla', 'sector']);
      setSendWhatsApp(formData.send_whatsapp_confirmation || false);
      setAssignedUsers(formData.assigned_users || []);
      setFields(formData.fields || []);
    } catch (error) {
      toast.error('Failed to load form');
      navigate('/forms');
    } finally {
      setLoading(false);
    }
  }, [api, formId, navigate]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  // Fetch available users for access control
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await api.get('/forms/users/list');
      setAvailableUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, [api]);

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Please enter a form name');
      return;
    }
    
    setSaving(true);
    try {
      await api.put(`/forms/${formId}`, {
        name: formName.trim(),
        description: formDescription.trim(),
        fields: fields.map((f, idx) => ({ ...f, order: idx })),
        is_open: isOpen,
        allow_multiple_submissions: allowMultiple,
        auto_fetch_fields: autoFetchFields,
        send_whatsapp_confirmation: sendWhatsApp,
        assigned_users: assignedUsers
      });
      toast.success('Form saved successfully!');
    } catch (error) {
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const toggleAutoFetchField = (fieldKey) => {
    setAutoFetchFields(prev => 
      prev.includes(fieldKey) 
        ? prev.filter(f => f !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const toggleUserAccess = (userId) => {
    setAssignedUsers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAddField = () => {
    if (!newFieldType) {
      toast.error('Please select a field type');
      return;
    }
    if (!newFieldLabel.trim()) {
      toast.error('Please enter a field label');
      return;
    }
    
    // Validate options for dropdown/checkbox
    if (['dropdown', 'checkbox'].includes(newFieldType) && !newFieldOptions.trim()) {
      toast.error('Please enter options (one per line)');
      return;
    }
    
    // Validate rating configuration
    if (newFieldType === 'rating') {
      if (newRatingMin >= newRatingMax) {
        toast.error('Maximum value must be greater than minimum');
        return;
      }
      if (!newRatingLeftLabel.trim() || !newRatingRightLabel.trim()) {
        toast.error('Please enter both endpoint labels');
        return;
      }
    }
    
    const newField = {
      id: `field_${Date.now()}`,
      type: newFieldType,
      label: newFieldLabel.trim(),
      required: newFieldRequired,
      placeholder: newFieldPlaceholder.trim() || null,
      description: newFieldDescription.trim() || null,
      options: ['dropdown', 'checkbox'].includes(newFieldType) 
        ? newFieldOptions.split('\n').map(o => o.trim()).filter(o => o)
        : null,
      // Rating config
      rating_config: newFieldType === 'rating' ? {
        min: parseInt(newRatingMin),
        max: parseInt(newRatingMax),
        step: parseInt(newRatingStep),
        left_label: newRatingLeftLabel.trim(),
        right_label: newRatingRightLabel.trim()
      } : null,
      order: fields.length
    };
    
    setFields([...fields, newField]);
    resetAddFieldDialog();
    toast.success('Field added');
  };

  const resetAddFieldDialog = () => {
    setShowAddFieldDialog(false);
    setNewFieldType('');
    setNewFieldLabel('');
    setNewFieldRequired(false);
    setNewFieldPlaceholder('');
    setNewFieldOptions('');
    setNewFieldDescription('');
    // Reset rating config
    setNewRatingMin(1);
    setNewRatingMax(5);
    setNewRatingStep(1);
    setNewRatingLeftLabel('');
    setNewRatingRightLabel('');
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setEditFieldLabel(field.label);
    setEditFieldRequired(field.required || false);
    setEditFieldPlaceholder(field.placeholder || '');
    setEditFieldOptions(field.options?.join('\n') || '');
    setEditFieldDescription(field.description || '');
    // Load rating config
    if (field.type === 'rating' && field.rating_config) {
      setEditRatingMin(field.rating_config.min || 1);
      setEditRatingMax(field.rating_config.max || 5);
      setEditRatingStep(field.rating_config.step || 1);
      setEditRatingLeftLabel(field.rating_config.left_label || '');
      setEditRatingRightLabel(field.rating_config.right_label || '');
    } else {
      setEditRatingMin(1);
      setEditRatingMax(5);
      setEditRatingStep(1);
      setEditRatingLeftLabel('');
      setEditRatingRightLabel('');
    }
    setShowEditFieldDialog(true);
  };

  const handleUpdateField = () => {
    if (!editFieldLabel.trim()) {
      toast.error('Please enter a field label');
      return;
    }
    
    if (['dropdown', 'checkbox'].includes(editingField.type) && !editFieldOptions.trim()) {
      toast.error('Please enter options');
      return;
    }
    
    // Validate rating configuration
    if (editingField.type === 'rating') {
      if (editRatingMin >= editRatingMax) {
        toast.error('Maximum value must be greater than minimum');
        return;
      }
      if (!editRatingLeftLabel.trim() || !editRatingRightLabel.trim()) {
        toast.error('Please enter both endpoint labels');
        return;
      }
    }
    
    setFields(fields.map(f => 
      f.id === editingField.id 
        ? {
            ...f,
            label: editFieldLabel.trim(),
            required: editFieldRequired,
            placeholder: editFieldPlaceholder.trim() || null,
            description: editFieldDescription.trim() || null,
            options: ['dropdown', 'checkbox'].includes(f.type)
              ? editFieldOptions.split('\n').map(o => o.trim()).filter(o => o)
              : f.options,
            rating_config: f.type === 'rating' ? {
              min: parseInt(editRatingMin),
              max: parseInt(editRatingMax),
              step: parseInt(editRatingStep),
              left_label: editRatingLeftLabel.trim(),
              right_label: editRatingRightLabel.trim()
            } : f.rating_config
          }
        : f
    ));
    
    setShowEditFieldDialog(false);
    setEditingField(null);
    toast.success('Field updated');
  };

  const handleDeleteField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
    toast.success('Field removed');
  };

  const moveField = (index, direction) => {
    const newFields = [...fields];
    const newIndex = index + direction;
    
    if (newIndex < 0 || newIndex >= fields.length) return;
    
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setFields(newFields);
  };

  const copyFormLink = () => {
    const link = `${window.location.origin}/public-form/${formId}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link)
        .then(() => toast.success('Form link copied!'))
        .catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = link;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          toast.success('Form link copied!');
        });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Form link copied!');
    }
  };

  const getFieldTypeIcon = (type) => {
    const fieldType = FIELD_TYPES.find(ft => ft.value === type);
    return fieldType?.icon || Type;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in pb-20" data-testid="form-builder-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/forms')}
            data-testid="back-to-forms-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-heading font-semibold text-slate-800 line-clamp-1">
              {formName || 'Untitled Form'}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={isOpen ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                {isOpen ? 'Open' : 'Closed'}
              </Badge>
              <span className="text-xs text-slate-400">{fields.length} fields</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={copyFormLink}>
            <Copy className="w-4 h-4 mr-1" />
            Copy Link
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => window.open(`/public-form/${formId}`, '_blank')}
          >
            <Eye className="w-4 h-4 mr-1" />
            Preview
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowSettingsDialog(true)}
            data-testid="form-settings-btn"
          >
            <Settings2 className="w-4 h-4" />
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-navy hover:bg-navy/90"
            data-testid="save-form-btn"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Main Content - Two Columns */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: Form Details & Fields */}
        <div className="lg:col-span-2 space-y-6">
          {/* Form Details Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Form Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="formName">Form Name *</Label>
                <Input
                  id="formName"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Enter form name"
                  data-testid="edit-form-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="formDesc">Description</Label>
                <Textarea
                  id="formDesc"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Brief description..."
                  rows={2}
                  data-testid="edit-form-description"
                />
              </div>
            </CardContent>
          </Card>

          {/* Fields Card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Form Fields</CardTitle>
                <Button 
                  size="sm" 
                  onClick={() => setShowAddFieldDialog(true)}
                  data-testid="add-field-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Field
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {fields.length === 0 ? (
                <div className="py-8 text-center border-2 border-dashed border-slate-200 rounded-lg">
                  <Type className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No fields yet</p>
                  <p className="text-sm text-slate-400">Click "Add Field" to start building your form</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {fields.map((field, index) => {
                    const FieldIcon = getFieldTypeIcon(field.type);
                    return (
                      <div 
                        key={field.id} 
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors"
                        data-testid={`field-item-${field.id}`}
                      >
                        <div className="flex flex-col gap-1">
                          <button 
                            onClick={() => moveField(index, -1)}
                            disabled={index === 0}
                            className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          </button>
                          <GripVertical className="w-4 h-4 text-slate-300" />
                          <button 
                            onClick={() => moveField(index, 1)}
                            disabled={index === fields.length - 1}
                            className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                        
                        <div className="w-8 h-8 bg-navy/10 rounded flex items-center justify-center flex-shrink-0">
                          <FieldIcon className="w-4 h-4 text-navy" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">{field.label}</span>
                            {field.required && (
                              <Badge variant="outline" className="text-xs text-red-500 border-red-200">Required</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-400 capitalize">{field.type.replace('_', ' ')}</span>
                            {field.options && (
                              <span className="text-xs text-slate-400">• {field.options.length} options</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEditField(field)}
                            className="h-8 w-8 p-0"
                          >
                            <Settings2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteField(field.id)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Preview & Help */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                onClick={() => window.open(`/public-form/${formId}`, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Public Form
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={copyFormLink}>
                <Copy className="w-4 h-4 mr-2" />
                Copy Shareable Link
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                onClick={() => navigate(`/form-results/${formId}`)}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                View Results & Analytics
              </Button>
              {isSuperAdmin && (
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  onClick={() => {
                    fetchUsers();
                    setShowAccessDialog(true);
                  }}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign User Access ({assignedUsers.length})
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Field Types Guide */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Available Field Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                {FIELD_TYPES.map(ft => (
                  <div key={ft.value} className="flex items-center gap-2 text-slate-600">
                    <ft.icon className="w-4 h-4 text-slate-400" />
                    <span className="font-medium">{ft.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-700">
                  <strong>Tip:</strong> Use "ITS Number" or "HOF ITS" fields to automatically fetch member data like name, mobile, mohalla, and sector.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Field Dialog */}
      <Dialog open={showAddFieldDialog} onOpenChange={setShowAddFieldDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="add-field-dialog">
          <DialogHeader>
            <DialogTitle>Add Field</DialogTitle>
            <DialogDescription>
              Choose a field type and configure it
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Field Type Selection */}
            <div className="space-y-2">
              <Label>Field Type *</Label>
              <div className="grid grid-cols-2 gap-2">
                {FIELD_TYPES.map(ft => (
                  <button
                    key={ft.value}
                    type="button"
                    onClick={() => setNewFieldType(ft.value)}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left ${
                      newFieldType === ft.value 
                        ? 'border-navy bg-navy/5' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <ft.icon className={`w-5 h-5 ${newFieldType === ft.value ? 'text-navy' : 'text-slate-400'}`} />
                    <div>
                      <p className="text-sm font-medium">{ft.label}</p>
                      <p className="text-xs text-slate-400">{ft.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Field Label */}
            <div className="space-y-2">
              <Label htmlFor="fieldLabel">Field Label *</Label>
              <Input
                id="fieldLabel"
                value={newFieldLabel}
                onChange={(e) => setNewFieldLabel(e.target.value)}
                placeholder="e.g., Full Name, Email Address..."
                data-testid="new-field-label"
              />
            </div>
            
            {/* Placeholder */}
            {['short_text', 'long_text', 'number'].includes(newFieldType) && (
              <div className="space-y-2">
                <Label htmlFor="fieldPlaceholder">Placeholder Text</Label>
                <Input
                  id="fieldPlaceholder"
                  value={newFieldPlaceholder}
                  onChange={(e) => setNewFieldPlaceholder(e.target.value)}
                  placeholder="e.g., Enter your name..."
                />
              </div>
            )}
            
            {/* Options for dropdown/checkbox */}
            {['dropdown', 'checkbox'].includes(newFieldType) && (
              <div className="space-y-2">
                <Label htmlFor="fieldOptions">Options (one per line) *</Label>
                <Textarea
                  id="fieldOptions"
                  value={newFieldOptions}
                  onChange={(e) => setNewFieldOptions(e.target.value)}
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                  rows={4}
                  data-testid="new-field-options"
                />
              </div>
            )}
            
            {/* Rating Scale Configuration */}
            {newFieldType === 'rating' && (
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-medium text-sm text-slate-700">Scale Configuration</h4>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Minimum Value</Label>
                    <Input
                      type="number"
                      value={newRatingMin}
                      onChange={(e) => setNewRatingMin(parseInt(e.target.value) || 1)}
                      min={0}
                      max={10}
                      data-testid="rating-min"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Maximum Value</Label>
                    <Input
                      type="number"
                      value={newRatingMax}
                      onChange={(e) => setNewRatingMax(parseInt(e.target.value) || 5)}
                      min={1}
                      max={10}
                      data-testid="rating-max"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Step</Label>
                    <Input
                      type="number"
                      value={newRatingStep}
                      onChange={(e) => setNewRatingStep(parseInt(e.target.value) || 1)}
                      min={1}
                      max={5}
                      data-testid="rating-step"
                    />
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-slate-700">Endpoint Labels</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Left Label (for {newRatingMin}) *</Label>
                      <Input
                        value={newRatingLeftLabel}
                        onChange={(e) => setNewRatingLeftLabel(e.target.value)}
                        placeholder="e.g., Least fruitful"
                        data-testid="rating-left-label"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Right Label (for {newRatingMax}) *</Label>
                      <Input
                        value={newRatingRightLabel}
                        onChange={(e) => setNewRatingRightLabel(e.target.value)}
                        placeholder="e.g., Very fruitful"
                        data-testid="rating-right-label"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Preview */}
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Preview</Label>
                  <div className="flex items-center justify-between gap-2 p-3 bg-white rounded border">
                    <span className="text-xs text-slate-500 min-w-[80px]">{newRatingLeftLabel || 'Left label'}</span>
                    <div className="flex gap-2">
                      {Array.from({ length: Math.ceil((newRatingMax - newRatingMin) / newRatingStep) + 1 }, (_, i) => {
                        const value = newRatingMin + (i * newRatingStep);
                        if (value > newRatingMax) return null;
                        return (
                          <button
                            key={value}
                            type="button"
                            className="w-8 h-8 rounded-full border-2 border-slate-300 text-sm font-medium text-slate-600 hover:border-navy hover:bg-navy/10"
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                    <span className="text-xs text-slate-500 min-w-[80px] text-right">{newRatingRightLabel || 'Right label'}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="fieldDescription">Help Text (optional)</Label>
              <Input
                id="fieldDescription"
                value={newFieldDescription}
                onChange={(e) => setNewFieldDescription(e.target.value)}
                placeholder="Additional instructions for this field"
              />
            </div>
            
            {/* Required toggle */}
            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="fieldRequired">Required Field</Label>
                <p className="text-xs text-slate-500">User must fill this field</p>
              </div>
              <Switch
                id="fieldRequired"
                checked={newFieldRequired}
                onCheckedChange={setNewFieldRequired}
                data-testid="new-field-required"
              />
            </div>
            
            {/* ITS Auto-fetch note */}
            {['its_number', 'hof_its_number'].includes(newFieldType) && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-700">
                  <strong>Auto-fetch enabled:</strong> When users enter an ITS number, the form will automatically display their Full Name, HOF Name, Mobile, Mohalla, Sector, and Address.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={resetAddFieldDialog}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddField}
              className="bg-navy hover:bg-navy/90"
              data-testid="confirm-add-field-btn"
            >
              Add Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Field Dialog */}
      <Dialog open={showEditFieldDialog} onOpenChange={setShowEditFieldDialog}>
        <DialogContent className="sm:max-w-md" data-testid="edit-field-dialog">
          <DialogHeader>
            <DialogTitle>Edit Field</DialogTitle>
            <DialogDescription>
              Update field settings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2 p-2 bg-slate-50 rounded">
              <span className="text-sm text-slate-500">Type:</span>
              <Badge variant="outline" className="capitalize">{editingField?.type?.replace('_', ' ')}</Badge>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="editLabel">Field Label *</Label>
              <Input
                id="editLabel"
                value={editFieldLabel}
                onChange={(e) => setEditFieldLabel(e.target.value)}
                data-testid="edit-field-label"
              />
            </div>
            
            {['short_text', 'long_text', 'number'].includes(editingField?.type) && (
              <div className="space-y-2">
                <Label htmlFor="editPlaceholder">Placeholder Text</Label>
                <Input
                  id="editPlaceholder"
                  value={editFieldPlaceholder}
                  onChange={(e) => setEditFieldPlaceholder(e.target.value)}
                />
              </div>
            )}
            
            {['dropdown', 'checkbox'].includes(editingField?.type) && (
              <div className="space-y-2">
                <Label htmlFor="editOptions">Options (one per line) *</Label>
                <Textarea
                  id="editOptions"
                  value={editFieldOptions}
                  onChange={(e) => setEditFieldOptions(e.target.value)}
                  rows={4}
                  data-testid="edit-field-options"
                />
              </div>
            )}
            
            {/* Rating Scale Configuration - Edit */}
            {editingField?.type === 'rating' && (
              <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-medium text-sm text-slate-700">Scale Configuration</h4>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Minimum Value</Label>
                    <Input
                      type="number"
                      value={editRatingMin}
                      onChange={(e) => setEditRatingMin(parseInt(e.target.value) || 1)}
                      min={0}
                      max={10}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Maximum Value</Label>
                    <Input
                      type="number"
                      value={editRatingMax}
                      onChange={(e) => setEditRatingMax(parseInt(e.target.value) || 5)}
                      min={1}
                      max={10}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Step</Label>
                    <Input
                      type="number"
                      value={editRatingStep}
                      onChange={(e) => setEditRatingStep(parseInt(e.target.value) || 1)}
                      min={1}
                      max={5}
                    />
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-slate-700">Endpoint Labels</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Left Label (for {editRatingMin}) *</Label>
                      <Input
                        value={editRatingLeftLabel}
                        onChange={(e) => setEditRatingLeftLabel(e.target.value)}
                        placeholder="e.g., Least fruitful"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Right Label (for {editRatingMax}) *</Label>
                      <Input
                        value={editRatingRightLabel}
                        onChange={(e) => setEditRatingRightLabel(e.target.value)}
                        placeholder="e.g., Very fruitful"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="editDescription">Help Text</Label>
              <Input
                id="editDescription"
                value={editFieldDescription}
                onChange={(e) => setEditFieldDescription(e.target.value)}
              />
            </div>
            
            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="editRequired">Required Field</Label>
              </div>
              <Switch
                id="editRequired"
                checked={editFieldRequired}
                onCheckedChange={setEditFieldRequired}
                data-testid="edit-field-required"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditFieldDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateField}
              className="bg-navy hover:bg-navy/90"
              data-testid="confirm-edit-field-btn"
            >
              Update Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="form-settings-dialog">
          <DialogHeader>
            <DialogTitle>Form Settings</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Form Status</Label>
                <p className="text-xs text-slate-500">Open forms accept submissions</p>
              </div>
              <Switch
                checked={isOpen}
                onCheckedChange={setIsOpen}
                data-testid="toggle-form-status"
              />
            </div>
            <div className={`text-sm ${isOpen ? 'text-emerald-600' : 'text-red-600'}`}>
              {isOpen ? 'Form is accepting submissions' : 'Form is closed'}
            </div>
            
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Allow Multiple Submissions</Label>
                  <p className="text-xs text-slate-500">If off, one submission per ITS</p>
                </div>
                <Switch
                  checked={allowMultiple}
                  onCheckedChange={setAllowMultiple}
                  data-testid="toggle-allow-multiple"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    WhatsApp Confirmation
                  </Label>
                  <p className="text-xs text-slate-500">Send confirmation to member on submission</p>
                </div>
                <Switch
                  checked={sendWhatsApp}
                  onCheckedChange={setSendWhatsApp}
                  data-testid="toggle-whatsapp"
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <Label className="mb-3 block">Auto-Fetch Member Fields</Label>
              <p className="text-xs text-slate-500 mb-3">Select which member data to fetch when ITS is entered and show in results/exports</p>
              <div className="grid grid-cols-2 gap-2">
                {AUTO_FETCH_OPTIONS.map(option => (
                  <div key={option.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`auto-${option.key}`}
                      checked={autoFetchFields.includes(option.key)}
                      onCheckedChange={() => toggleAutoFetchField(option.key)}
                    />
                    <label htmlFor={`auto-${option.key}`} className="text-sm">{option.label}</label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="border-t pt-4">
              <Label className="text-slate-500">Form ID</Label>
              <p className="font-mono text-sm mt-1">{formId}</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowSettingsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Access Dialog */}
      <Dialog open={showAccessDialog} onOpenChange={setShowAccessDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="user-access-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Assign User Access
            </DialogTitle>
            <DialogDescription>
              Select users who can view this form's results and exports
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Search Input */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or email..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="user-search-input"
              />
            </div>
            
            {loadingUsers ? (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <p className="text-center text-slate-500 py-4">
                {userSearchQuery ? 'No users found matching your search' : 'No users available'}
              </p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {filteredUsers.map(user => (
                  <div 
                    key={user.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      assignedUsers.includes(user.id) ? 'bg-navy/5 border-navy' : 'hover:bg-slate-50 border-slate-200'
                    }`}
                    onClick={() => toggleUserAccess(user.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={assignedUsers.includes(user.id)}
                        onCheckedChange={() => toggleUserAccess(user.id)}
                      />
                      <div>
                        <p className="font-medium text-sm">{user.full_name}</p>
                        <p className="text-xs text-slate-500">{user.email || 'No email'}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs capitalize">{user.role?.replace('_', ' ')}</Badge>
                  </div>
                ))}
              </div>
            )}
            
            {assignedUsers.length > 0 && (
              <p className="text-sm text-emerald-600 mt-3">
                {assignedUsers.length} user(s) will have access to results
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccessDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                setShowAccessDialog(false);
                toast.success(`Access updated for ${assignedUsers.length} user(s). Don't forget to save!`);
              }}
              className="bg-navy hover:bg-navy/90"
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
